import type {
  BrightSitesCatalogProposal,
  BrightSitesProgressionProposal,
} from '@/services/warehouseTypes'

export type CatalogRowState = {
  accepted: boolean
  proposedName: string
}

export type ProgressionRowState = {
  accepted: boolean
  caseCost: string
  linkedManualEntryId?: number | null
  linkedMovementId?: number | null
  linkedLayerId?: number | null
}

export type ProgressionLedgerCandidateSource = 'manual' | 'movement' | 'layer'

export type ProgressionLedgerCandidate = {
  ledgerTxnId: number
  kind: 'opening' | 'manufacturer' | 'shipment' | 'adjustment'
  sku: string
  date: string
  cases: number
  bsLogId: number | null
  source: ProgressionLedgerCandidateSource
}

export type ProgressionLedgerCandidateInput = {
  linkableLedgerTxnId: number | null
  linkableSource: ProgressionLedgerCandidateSource | null
  kind: 'opening' | 'manufacturer' | 'shipment' | 'adjustment'
  sku: string
  date: string
  cases: number
  bsLogId: number | null
}

type LinkTargetState = Pick<
  ProgressionRowState,
  'linkedManualEntryId' | 'linkedMovementId' | 'linkedLayerId'
>

const SUPPLY_KINDS = new Set(['opening', 'adjustment', 'manufacturer'])

export function catalogRowKey(row: BrightSitesCatalogProposal, index: number): string {
  if (row.proposal_type === 'name_mismatch' && row.crosswalk_id != null) {
    return `name-${row.crosswalk_id}`
  }
  if (row.full_sku) return `${row.proposal_type}-${row.full_sku}`
  if (row.bk_sku) return `${row.proposal_type}-${row.bk_sku}`
  return `catalog-${index}`
}

export function defaultCatalogAccepted(row: BrightSitesCatalogProposal): boolean {
  return row.proposal_type === 'missing_in_catalog' || row.proposal_type === 'name_mismatch'
}

export function defaultProposedName(row: BrightSitesCatalogProposal): string {
  return row.proposed_display_name ?? row.current_display_name ?? row.full_sku ?? ''
}

export function catalogSku(row: BrightSitesCatalogProposal): string {
  return row.full_sku ?? row.bk_sku ?? row.bright_catalog_sku ?? row.wh_sku ?? '—'
}

export function isActionableProgressionRow(row: BrightSitesProgressionProposal): boolean {
  if (row.kind === 'return') return true
  if (row.status === 'qty_mismatch') return true
  if (row.status === 'already_on_ledger') return true
  if (row.status === 'new' && (row.kind === 'opening' || row.kind === 'adjustment')) return true
  return false
}

export function defaultProgressionAccepted(row: BrightSitesProgressionProposal): boolean {
  if (row.status === 'qty_mismatch') return true
  return row.status === 'new' && (row.kind === 'opening' || row.kind === 'adjustment')
}

export function progressionRowSku(row: BrightSitesProgressionProposal): string {
  return (row.bk_sku ?? row.product_sku).trim()
}

export function getEffectiveLinkedManualEntryId(
  row: BrightSitesProgressionProposal,
  state?: LinkTargetState
): number | null {
  if (state && 'linkedManualEntryId' in state) {
    return state.linkedManualEntryId ?? null
  }
  return row.linked_manual_entry_id ?? null
}

export function getEffectiveLinkedMovementId(
  row: BrightSitesProgressionProposal,
  state?: LinkTargetState
): number | null {
  if (state && 'linkedMovementId' in state) {
    return state.linkedMovementId ?? null
  }
  return row.linked_movement_id ?? null
}

export function getEffectiveLinkedLayerId(
  row: BrightSitesProgressionProposal,
  state?: LinkTargetState
): number | null {
  if (state && 'linkedLayerId' in state) {
    return state.linkedLayerId ?? null
  }
  return row.linked_layer_id ?? null
}

export function progressionEffectiveLedgerId(
  row: BrightSitesProgressionProposal,
  state?: LinkTargetState
): number | null {
  if (row.kind === 'shipment') return getEffectiveLinkedMovementId(row, state)
  return (
    getEffectiveLinkedManualEntryId(row, state) ?? getEffectiveLinkedLayerId(row, state)
  )
}

export function progressionEffectiveLedgerValue(
  row: BrightSitesProgressionProposal,
  state?: LinkTargetState
): string {
  const movementId = getEffectiveLinkedMovementId(row, state)
  if (row.kind === 'shipment') {
    return movementId != null ? `movement:${movementId}` : ''
  }
  const manualId = getEffectiveLinkedManualEntryId(row, state)
  if (manualId != null) return `manual:${manualId}`
  const layerId = getEffectiveLinkedLayerId(row, state)
  if (layerId != null) return `layer:${layerId}`
  return ''
}

export function progressionHasLinkTarget(
  row: BrightSitesProgressionProposal,
  state?: LinkTargetState
): boolean {
  return progressionEffectiveLedgerId(row, state) != null
}

export function progressionCanReconcile(row: BrightSitesProgressionProposal): boolean {
  return (
    row.status === 'qty_mismatch' &&
    (row.linked_manual_entry_id != null || row.linked_layer_id != null)
  )
}

export function progressionCanApply(
  row: BrightSitesProgressionProposal,
  state?: LinkTargetState
): boolean {
  if (progressionHasLinkTarget(row, state)) return false
  return row.status === 'new' && (row.kind === 'opening' || row.kind === 'adjustment')
}

export function progressionCanLink(
  row: BrightSitesProgressionProposal,
  state?: LinkTargetState
): boolean {
  if (row.status === 'already_on_ledger') return true
  return progressionHasLinkTarget(row, state)
}

export function progressionIsSelectable(
  row: BrightSitesProgressionProposal,
  state?: LinkTargetState
): boolean {
  return (
    progressionCanApply(row, state) ||
    progressionCanLink(row, state) ||
    progressionCanReconcile(row)
  )
}

export function catalogCanApply(row: BrightSitesCatalogProposal): boolean {
  return row.proposal_type === 'missing_in_catalog' || row.proposal_type === 'name_mismatch'
}

export function catalogTypeLabel(type: BrightSitesCatalogProposal['proposal_type']): string {
  switch (type) {
    case 'missing_in_catalog':
      return 'Missing in catalog'
    case 'missing_in_brightsites':
      return 'Missing in Bright Sites'
    case 'name_mismatch':
      return 'Name mismatch'
    default:
      return type
  }
}

export function progressionStatusLabel(
  row: BrightSitesProgressionProposal,
  state?: LinkTargetState
): string {
  if (row.status === 'qty_mismatch') return 'Reconcile qty'
  if (row.status === 'already_on_ledger' || progressionHasLinkTarget(row, state)) {
    if (row.kind === 'shipment') return 'Link shipment'
    if (row.kind === 'opening') return 'Link opening'
    if (row.kind === 'adjustment') return 'Link adjustment'
    return 'Link'
  }
  if (row.status === 'new') {
    if (row.kind === 'opening') return 'Add opening'
    if (row.kind === 'adjustment') return 'Add adjustment'
    if (row.kind === 'return') return 'Import via ledger'
    return 'New'
  }
  if (row.status === 'linked') return 'Linked'
  return row.status
}

export function progressionDifferenceNotes(row: BrightSitesProgressionProposal): string {
  if (row.status === 'qty_mismatch') {
    return (
      row.reason ??
      `Ledger shows ${formatProgressionCases(row.ledger_cases)} cases; Bright Sites shows ${formatProgressionCases(row.cases)}.`
    )
  }
  if (progressionCanLink(row)) {
    if (row.kind === 'shipment') {
      return row.reason ?? 'Link this Bright Sites sold log to the existing billing movement.'
    }
    if (row.kind === 'opening') {
      return row.reason ?? 'Link this store seed to the existing opening stock row.'
    }
    return row.reason ?? 'Link this Bright Sites log to the existing ledger row.'
  }
  if (progressionCanApply(row) && row.kind === 'opening') {
    return 'Store → warehouse seed supply.'
  }
  return row.reason ?? ''
}

export function formatProgressionCases(cases: number | null | undefined): string {
  if (cases == null || !Number.isFinite(Number(cases))) return '—'
  return String(Math.round(Number(cases)))
}

export function formatLedgerCandidateLabel(candidate: ProgressionLedgerCandidate): string {
  const cases = formatProgressionCases(candidate.cases)
  return `${candidate.ledgerTxnId} · ${candidate.date || '—'} · ${cases} cases`
}

export function ledgerCandidateValue(candidate: ProgressionLedgerCandidate): string {
  return `${candidate.source}:${candidate.ledgerTxnId}`
}

export function parseLedgerCandidateValue(
  value: string
): { source: ProgressionLedgerCandidateSource; ledgerTxnId: number } | null {
  const [source, idText] = value.split(':')
  if (source !== 'manual' && source !== 'movement' && source !== 'layer') return null
  const ledgerTxnId = Number(idText)
  if (!Number.isFinite(ledgerTxnId)) return null
  return { source, ledgerTxnId }
}

export function buildProgressionLedgerCandidates(
  rows: ProgressionLedgerCandidateInput[]
): ProgressionLedgerCandidate[] {
  const candidates: ProgressionLedgerCandidate[] = []
  for (const row of rows) {
    if (row.linkableLedgerTxnId == null || row.linkableSource == null) continue
    if (
      row.kind !== 'opening' &&
      row.kind !== 'shipment' &&
      row.kind !== 'adjustment' &&
      row.kind !== 'manufacturer'
    ) {
      continue
    }
    candidates.push({
      ledgerTxnId: row.linkableLedgerTxnId,
      kind: row.kind,
      sku: row.sku,
      date: row.date,
      cases: row.cases,
      bsLogId: row.bsLogId,
      source: row.linkableSource,
    })
  }
  return candidates
}

export function filterLedgerCandidatesForRow(
  candidates: ProgressionLedgerCandidate[],
  row: BrightSitesProgressionProposal,
  state?: LinkTargetState
): ProgressionLedgerCandidate[] {
  const sku = progressionRowSku(row).toLowerCase()
  const selectedValue = progressionEffectiveLedgerValue(row, state)

  return candidates
    .filter((candidate) => {
      if (row.kind === 'shipment') {
        if (candidate.kind !== 'shipment') return false
      } else if (!SUPPLY_KINDS.has(candidate.kind)) {
        return false
      }
      if (candidate.sku.trim().toLowerCase() !== sku) return false
      const unlinked = candidate.bsLogId == null || candidate.bsLogId === 0
      const isSelected = selectedValue === ledgerCandidateValue(candidate)
      return unlinked || isSelected
    })
    .sort((a, b) => a.ledgerTxnId - b.ledgerTxnId)
}

export function buildInitialCatalogState(
  catalog: BrightSitesCatalogProposal[]
): Record<string, CatalogRowState> {
  const state: Record<string, CatalogRowState> = {}
  catalog.forEach((row, index) => {
    state[catalogRowKey(row, index)] = {
      accepted: defaultCatalogAccepted(row),
      proposedName: defaultProposedName(row),
    }
  })
  return state
}

export function buildInitialProgressionState(
  progression: BrightSitesProgressionProposal[]
): Record<number, ProgressionRowState> {
  const state: Record<number, ProgressionRowState> = {}
  for (const row of progression) {
    state[row.bs_log_id] = {
      accepted: defaultProgressionAccepted(row),
      caseCost: '',
      linkedManualEntryId: row.linked_manual_entry_id ?? null,
      linkedMovementId: row.linked_movement_id ?? null,
      linkedLayerId: row.linked_layer_id ?? null,
    }
  }
  return state
}
